import './pricing-options-page.css';

import { classifyPlanSelection, planDesignMonths, resolvePlanDisplay } from '@cadstrom/backend/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';

import { KeepCurrentPlanButton } from '#components/billing/keep-current-plan-button.js';
import { resolveShowBillingFlow } from '#components/feature-flag/use-show-billing-flow.js';
import { useTRPC } from '#src/trpc.js';
import { useAbilityContext } from '#state/contexts/ability-context.js';
import { formatPlanChangeDate } from '#utils/billing-dates.js';
import { RouteAuthorizationError } from '#utils/route-authorization.js';
import { PlanCardGrid } from './components/plan-card-grid.js';
import { SubscriptionDowngradeStep } from './components/subscription-downgrade-step.js';
import { SubscriptionPaymentStep } from './components/subscription-payment-step.js';
import { SubscriptionThankYou } from './components/subscription-thank-you.js';

import type { AvailableSubscriptionPlanDto } from '@cadstrom/backend/shared';

export const Route = createFileRoute('/_authorization-gate/orgs/$orgName/billing/plans/')({
	validateSearch: z.object({
		step: z.enum(['plans', 'payment', 'downgrade', 'thank-you']).catch('plans').default('plans'),
		planAlias: z.string().optional(),
		returnTo: z.string().optional(),
		projectId: z.string().optional(),
		buildId: z.string().optional(),
		/** In-flight purchase being confirmed — keeps the checkout context across a refresh. */
		purchaseRecordId: z.string().optional(),
		/** Set by Stripe on the return from a redirect-based card confirmation (3DS). */
		redirect_status: z.string().optional(),
		analyzed: z
			.union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')])
			.optional()
			.catch(undefined),
	}),
	staticData: {
		authorize: (ability) => ability.can('view', 'organization'),
	},
	beforeLoad: async ({ context, params }) => {
		const currentUser = await context.queryClient.ensureQueryData(context.trpc.user.getCurrent.queryOptions());
		const isOrgMember = currentUser.memberships.some((m) => m.groupUsername === params.orgName);
		if (!currentUser.auth.isStaff && !isOrgMember) {
			throw new RouteAuthorizationError();
		}
		if (!(await resolveShowBillingFlow(context.queryClient, context.trpc, context.posthog, params.orgName))) {
			throw redirect({ to: '/orgs/$orgName', params: { orgName: params.orgName } });
		}
	},
	head: () => ({ meta: [{ title: 'Plans • Cadstrom' }] }),
	component: PricingOptionsPage,
});

/**
 * Rewrite the results-page `tab` query param of a return href so it points at
 * the Results tab. The correct tab id depends on the requirements mode carried
 * in the href: the legacy `?reqv1=true` view uses `analysis-results`, the
 * default V2 view uses `v2-results`.
 *
 * @param returnTo - The absolute or root-relative href to return to.
 * @returns The href with its `tab` param set to the Results tab, or the input unchanged if it cannot be parsed.
 */
function withResultsTab(returnTo: string): string {
	try {
		const url = new URL(returnTo, window.location.origin);
		const isReqV1 = url.searchParams.get('reqv1') === 'true';
		url.searchParams.set('tab', isReqV1 ? 'analysis-results' : 'v2-results');
		return url.pathname + url.search + url.hash;
	} catch {
		return returnTo;
	}
}

function PricingOptionsPage() {
	const { orgName } = Route.useParams();
	const { step, planAlias, returnTo, projectId, buildId, analyzed, purchaseRecordId, redirect_status } =
		Route.useSearch();
	const navigate = useNavigate();
	const trpc = useTRPC();
	const ability = useAbilityContext();

	const { data: orgData } = useSuspenseQuery(trpc.organization.get.queryOptions({ organizationName: orgName }));
	const { data: plans } = useSuspenseQuery(
		trpc.billing.getSubscriptionPlans.queryOptions({ organizationId: orgData.organization.id }),
	);

	const canSubscribe = ability.can('manage', 'organization');

	// An analyze target selects the curated subset; a direct visit lists everything.
	const planContext = projectId !== undefined && buildId !== undefined ? 'analyze' : 'admin';

	const handleNotNow = () => {
		if (returnTo !== undefined) {
			// `returnTo` is a raw href that may carry a query string; `to` would
			// treat the whole string as a pathname and miss the route. When an
			// analysis was started, force the results tab so the customer lands on
			// results rather than whichever tab they left from.
			void navigate({ href: analyzed === true ? withResultsTab(returnTo) : returnTo });
			return;
		}
		void navigate({ to: '/orgs/$orgName', params: { orgName } });
	};

	const handleSubscribe = (plan: AvailableSubscriptionPlanDto) => {
		// A downgrade schedules a future change and takes no payment, so it gets
		// its own confirmation step instead of the checkout.
		const relation = classifyPlanSelection(plans.current, {
			alias: plan.alias,
			designMonths: planDesignMonths(plan.designMonthsMaxCommit),
		});
		void navigate({
			to: '/orgs/$orgName/billing/plans',
			params: { orgName },
			search: {
				step: relation === 'downgrade' ? 'downgrade' : 'payment',
				planAlias: plan.alias,
				returnTo,
				projectId,
				buildId,
			},
		});
	};

	const handleDowngradeDone = () => {
		if (returnTo !== undefined) {
			void navigate({ href: returnTo });
			return;
		}
		void navigate({ href: `/orgs/${orgName}/settings?tab=billing` });
	};

	const handleBackToPlans = () => {
		void navigate({
			to: '/orgs/$orgName/billing/plans',
			params: { orgName },
			search: { step: 'plans', returnTo, projectId, buildId },
		});
	};

	const handlePaymentComplete = (analysisStarted: boolean) => {
		void navigate({
			to: '/orgs/$orgName/billing/plans',
			params: { orgName },
			search: { step: 'thank-you', returnTo, analyzed: analysisStarted, projectId, buildId },
		});
	};

	// Persist the in-flight purchase in the URL (replace, not push) so a
	// refresh or a shared link lands back on the confirming-payment state.
	const handlePurchasePending = (pendingPurchaseRecordId: string) => {
		void navigate({
			to: '/orgs/$orgName/billing/plans',
			params: { orgName },
			search: { step: 'payment', planAlias, returnTo, projectId, buildId, purchaseRecordId: pendingPurchaseRecordId },
			replace: true,
		});
	};

	const selectedPlan = plans.available.find((plan) => plan.alias === planAlias);
	const currentPlanTitle =
		plans.current.alias === null ? 'Custom plan' : resolvePlanDisplay(plans.current.alias, false).title;

	const brandMark = (
		<div className='flex items-center gap-2.5'>
			<img src='/images/Cadstrom_DARK_PURPLE_LOGOMARK.png' alt='' className='h-5 w-5 shrink-0 object-contain' />
			<span className='text-2xs font-semibold tracking-[0.18em] text-gray-a11 uppercase'>Cadstrom</span>
		</div>
	);

	if (plans.current.isSalesManaged) {
		return (
			<div className='pricing-options-canvas flex min-h-0 w-full grow flex-col items-center overflow-y-auto px-8 pt-14 pb-16'>
				{brandMark}
				<div className='flex w-full max-w-2xl grow flex-col items-center justify-center gap-4 text-center'>
					<h1 className='text-5xl font-bold tracking-[-0.015em]'>Your plan is managed by our sales team</h1>
					<p className='max-w-md leading-relaxed text-gray-a11'>
						Plan changes for your organization go through your account manager. Reach out to sales to adjust your
						subscription.
					</p>
					<Button variant='contained' href='mailto:sales@cadstrom.io'>
						<span>Contact sales</span>
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className='pricing-options-canvas flex min-h-0 w-full grow flex-col items-center overflow-y-auto px-8 pt-14 pb-16'>
			{brandMark}

			{step === 'thank-you' ?
				<SubscriptionThankYou
					analysisStarted={analyzed ?? false}
					backLabel={planContext === 'analyze' ? 'Back to results' : 'Go to billing management'}
					onBack={handleNotNow}
				/>
			:	<>
					<h1 className='mt-6 text-center text-5xl font-bold tracking-[-0.015em]'>
						{selectedPlan === undefined ?
							'Choose your plan'
						: step === 'payment' ?
							'Complete your subscription'
						: step === 'downgrade' ?
							'Change your plan'
						:	'Choose your plan'}
					</h1>
					{(step === 'plans' || selectedPlan === undefined) && (
						<p className='mt-3.5 max-w-lg text-center leading-relaxed text-pretty text-gray-a11'>
							Analysing a design uses one design month. Pick a plan to run your first analysis.
						</p>
					)}

					{!canSubscribe && (
						<Alert severity='info' className='mt-6 w-full max-w-3xl'>
							Only organization admins can change the subscription. Ask your admin to pick a plan.
						</Alert>
					)}

					{(step === 'plans' || selectedPlan === undefined) && plans.current.pendingPlanChange !== null && (
						<Alert
							severity='info'
							className='mt-6 w-full max-w-3xl [&_.MuiAlert-action]:shrink-0 [&_.MuiAlert-action]:self-center'
							action={
								canSubscribe && plans.current.pendingPlanChange.scheduledBy === 'customer' ?
									<KeepCurrentPlanButton
										organizationId={orgData.organization.id}
										currentPlanTitle={currentPlanTitle}
										pendingChange={plans.current.pendingPlanChange}
									/>
								:	undefined
							}
						>
							Your plan changes to {resolvePlanDisplay(plans.current.pendingPlanChange.targetAlias, false).title} on{' '}
							{formatPlanChangeDate(plans.current.pendingPlanChange.effectiveAt)}. Another change can be made once it
							takes effect
							{plans.current.pendingPlanChange.scheduledBy === 'customer' ? ', or keep your current plan' : ''}.
						</Alert>
					)}

					{(step === 'plans' || selectedPlan === undefined) && (
						<PlanCardGrid
							plans={plans}
							canSubscribe={canSubscribe}
							subscribingPlanAlias={null}
							context={planContext}
							onSubscribe={handleSubscribe}
							onNotNow={handleNotNow}
						/>
					)}

					{step === 'payment' && selectedPlan !== undefined && (
						<SubscriptionPaymentStep
							// A different plan is a different purchase: remount so the
							// payment phase cannot keep watching the previous plan's attempt.
							key={planAlias}
							organizationId={orgData.organization.id}
							contractId={plans.current.contractId}
							plan={selectedPlan}
							returnUrl={window.location.href}
							analyzeTarget={projectId !== undefined && buildId !== undefined ? { projectId, buildId } : undefined}
							initialPurchaseRecordId={purchaseRecordId}
							resumeAfterRedirect={redirect_status !== undefined}
							onPurchasePending={handlePurchasePending}
							onComplete={handlePaymentComplete}
							onBack={handleBackToPlans}
						/>
					)}

					{step === 'downgrade' && selectedPlan !== undefined && (
						<SubscriptionDowngradeStep
							// A different plan is a different change: remount so no phase
							// state leaks between targets.
							key={planAlias}
							organizationId={orgData.organization.id}
							current={plans.current}
							plan={selectedPlan}
							onDone={handleDowngradeDone}
							onBack={handleBackToPlans}
						/>
					)}
				</>
			}
		</div>
	);
}
